import { path } from "./usersPath";

import type { ILoaderData as IPatientLoaderData } from "@pages/panelPages/userManagement/patient/patientForm";
import type { ILoaderData as IDoctorLoaderData } from "@pages/panelPages/userManagement/doctor/doctorForm";
import type { IExtendedRouteObject } from "@routes/types.ts";
import type { ILoaderData as ILocalAdminLoaderData } from "@pages/panelPages/userManagement/localAdmin/localAdminForm";
import type { ILoaderData as IAdminLoaderData } from "@pages/panelPages/userManagement/admin/adminForm";
import type { ILoaderData as IStaffLoaderData } from "@pages/panelPages/userManagement/staff/staffForm";
import { useUIStore } from "@/stores/uiStore";

export const usersRoutes: IExtendedRouteObject[] = [
  {
    route: {
      path: path.users.route,
      lazy: () => import("@pages/panelPages/userManagement/users/usersList"),
      loader: ({ request }) => {
        useUIStore
          .getState()
          .setPage(request, "pageTitle.usersManagement", [], "index", "main");
        return {};
      },
    },
    permissions: ["super-admin", "admin"],
  },
  {
    route: {
      path: path.localAdmin.route,
      lazy: () =>
        import("@pages/panelPages/userManagement/localAdmin/localAdminList"),
      loader: ({ request }) => {
        useUIStore
          .getState()
          .setPage(
            request,
            "pageTitle.localAdminManagement",
            [],
            "index",
            "main",
          );
        return {};
      },
    },
    permissions: ["super-admin", "admin"],
  },
  {
    route: {
      path: path.localAdminView.route,
      lazy: () =>
        import("@pages/panelPages/userManagement/localAdmin/localAdminForm"),
      loader: ({ params, request }): ILocalAdminLoaderData => {
        useUIStore.getState().setPage(
          request,
          "pageTitle.localAdminView",
          [
            {
              url: path.localAdmin.link(),
              title: "pageTitle.localAdminManagement",
            },
          ],
          "view",
        );
        return { mode: "view", id: params.id! };
      },
    },
    permissions: ["super-admin", "admin"],
  },
  {
    route: {
      path: path.localAdminEdit.route,
      lazy: () =>
        import("@pages/panelPages/userManagement/localAdmin/localAdminForm"),
      loader: ({ params, request }): ILocalAdminLoaderData => {
        useUIStore.getState().setPage(
          request,
          "pageTitle.localAdminEdit",
          [
            {
              url: path.localAdmin.link(),
              title: "pageTitle.localAdminManagement",
            },
          ],
          "edit",
        );
        return { mode: "edit", id: params.id! };
      },
    },
    permissions: ["super-admin", "admin"],
  },
  {
    route: {
      path: path.localAdminCreate.route,
      lazy: () =>
        import("@pages/panelPages/userManagement/localAdmin/localAdminForm"),
      loader: ({ request }): ILocalAdminLoaderData => {
        useUIStore.getState().setPage(
          request,
          "pageTitle.localAdminCreate",
          [
            {
              url: path.localAdmin.link(),
              title: "pageTitle.localAdminManagement",
            },
          ],
          "create",
        );
        return { mode: "create" };
      },
    },
    permissions: ["super-admin", "admin"],
  },
  {
    route: {
      path: path.admin.route,
      lazy: () => import("@pages/panelPages/userManagement/admin/adminList"),
      loader: ({ request }) => {
        useUIStore
          .getState()
          .setPage(request, "pageTitle.adminManagement", [], "index", "main");
        return {};
      },
    },
    permissions: ["super-admin"],
  },
  {
    route: {
      path: path.adminView.route,
      lazy: () => import("@pages/panelPages/userManagement/admin/adminForm"),
      loader: ({ params, request }): IAdminLoaderData => {
        useUIStore
          .getState()
          .setPage(
            request,
            "pageTitle.adminView",
            [{ title: "pageTitle.adminManagement", url: path.admin.link() }],
            "view",
          );
        return { mode: "view", id: params.id! };
      },
    },
    permissions: ["super-admin"],
  },
  {
    route: {
      path: path.adminEdit.route,
      lazy: () => import("@pages/panelPages/userManagement/admin/adminForm"),
      loader: ({ params, request }): IAdminLoaderData => {
        useUIStore
          .getState()
          .setPage(
            request,
            "pageTitle.adminEdit",
            [{ title: "pageTitle.adminManagement", url: path.admin.link() }],
            "edit",
          );
        return { mode: "edit", id: params.id! };
      },
    },
    permissions: ["super-admin"],
  },
  {
    route: {
      path: path.adminCreate.route,
      lazy: () => import("@pages/panelPages/userManagement/admin/adminForm"),
      loader: ({ request }): IAdminLoaderData => {
        useUIStore
          .getState()
          .setPage(
            request,
            "pageTitle.adminCreate",
            [{ title: "pageTitle.adminManagement", url: path.admin.link() }],
            "create",
          );
        return { mode: "create" };
      },
    },
    permissions: ["super-admin"],
  },
  {
    route: {
      path: path.doctor.route,
      lazy: () => import("@pages/panelPages/userManagement/doctor/doctorList"),
      loader: ({ request }) => {
        useUIStore
          .getState()
          .setPage(request, "pageTitle.doctorManagement", [], "index", "main");
        return {};
      },
    },
    permissions: ["super-admin", "admin", "local-admin"],
  },
  {
    route: {
      path: path.doctorView.route,
      lazy: () => import("@pages/panelPages/userManagement/doctor/doctorForm"),
      loader: ({ params, request }): IDoctorLoaderData => {
        useUIStore
          .getState()
          .setPage(
            request,
            "pageTitle.doctorView",
            [{ title: "pageTitle.doctorManagement", url: path.doctor.link() }],
            "view",
          );
        return { mode: "view", id: params.id! };
      },
    },
    permissions: ["super-admin", "admin", "local-admin"],
  },
  {
    route: {
      path: path.doctorEdit.route,
      lazy: () => import("@pages/panelPages/userManagement/doctor/doctorForm"),
      loader: ({ params, request }): IDoctorLoaderData => {
        useUIStore
          .getState()
          .setPage(
            request,
            "pageTitle.doctorEdit",
            [{ title: "pageTitle.doctorManagement", url: path.doctor.link() }],
            "edit",
          );
        return { mode: "edit", id: params.id! };
      },
    },
    permissions: ["super-admin", "admin", "local-admin"],
  },
  {
    route: {
      path: path.doctorEditFacility.route,
      lazy: () =>
        import("@pages/panelPages/userManagement/doctor/doctorFacilityForm"),
      loader: ({ params, request }): IDoctorLoaderData => {
        useUIStore
          .getState()
          .setPage(
            request,
            "pageTitle.doctorEditFacility",
            [{ title: "pageTitle.doctorManagement", url: path.doctor.link() }],
            "edit",
          );
        return { mode: "edit", id: params.id! };
      },
    },
    permissions: ["super-admin", "admin", "local-admin"],
  },
  {
    route: {
      path: path.doctorCreate.route,
      lazy: () => import("@pages/panelPages/userManagement/doctor/doctorForm"),
      loader: ({ request }): IDoctorLoaderData => {
        useUIStore
          .getState()
          .setPage(
            request,
            "pageTitle.doctorCreate",
            [{ title: "pageTitle.doctorManagement", url: path.doctor.link() }],
            "create",
          );
        return { mode: "create" };
      },
    },
    permissions: ["super-admin", "admin", "local-admin"],
  },
  {
    route: {
      path: path.staff.route,
      lazy: () => import("@pages/panelPages/userManagement/staff/staffList"),
      loader: ({ request }) => {
        useUIStore
          .getState()
          .setPage(request, "pageTitle.staffManagement", [], "index", "main");
        return {};
      },
    },
    permissions: ["super-admin", "admin", "local-admin"],
  },
  {
    route: {
      path: path.staffView.route,
      lazy: () => import("@pages/panelPages/userManagement/staff/staffForm"),
      loader: ({ params, request }): IStaffLoaderData => {
        useUIStore
          .getState()
          .setPage(
            request,
            "pageTitle.staffView",
            [{ title: "pageTitle.staffManagement", url: path.staff.link() }],
            "view",
          );
        return { mode: "view", id: params.id! };
      },
    },
    permissions: ["super-admin", "admin", "local-admin"],
  },
  {
    route: {
      path: path.staffEdit.route,
      lazy: () => import("@pages/panelPages/userManagement/staff/staffForm"),
      loader: ({ params, request }): IStaffLoaderData => {
        useUIStore
          .getState()
          .setPage(
            request,
            "pageTitle.staffEdit",
            [{ title: "pageTitle.staffManagement", url: path.staff.link() }],
            "edit",
          );
        return { mode: "edit", id: params.id! };
      },
    },
    permissions: ["super-admin", "admin", "local-admin"],
  },
  {
    route: {
      path: path.staffCreate.route,
      lazy: () => import("@pages/panelPages/userManagement/staff/staffForm"),
      loader: ({ request }): IStaffLoaderData => {
        useUIStore
          .getState()
          .setPage(
            request,
            "pageTitle.staffCreate",
            [{ title: "pageTitle.staffManagement", url: path.staff.link() }],
            "create",
          );
        return { mode: "create" };
      },
    },
    permissions: ["super-admin", "admin", "local-admin"],
  },
  {
    route: {
      path: path.patient.route,
      lazy: () =>
        import("@pages/panelPages/userManagement/patient/patientList"),
      loader: ({ request }) => {
        useUIStore
          .getState()
          .setPage(request, "pageTitle.patientManagement", [], "index", "main");
        return {};
      },
    },
    permissions: ["super-admin", "admin", "local-admin"],
  },
  {
    route: {
      path: path.patientView.route,
      lazy: () =>
        import("@pages/panelPages/userManagement/patient/patientForm"),
      loader: ({ params, request }): IPatientLoaderData => {
        useUIStore.getState().setPage(
          request,
          "pageTitle.patientView",
          [
            {
              title: "pageTitle.patientManagement",
              url: path.patient.link(),
            },
          ],
          "view",
        );
        return { mode: "view", id: params.id! };
      },
    },
    permissions: ["super-admin", "admin", "local-admin"],
  },
  {
    route: {
      path: path.patientEdit.route,
      lazy: () =>
        import("@pages/panelPages/userManagement/patient/patientForm"),
      loader: ({ params, request }): IPatientLoaderData => {
        useUIStore.getState().setPage(
          request,
          "pageTitle.patientEdit",
          [
            {
              title: "pageTitle.patientManagement",
              url: path.patient.link(),
            },
          ],
          "edit",
        );
        return { mode: "edit", id: params.id! };
      },
    },
    permissions: ["super-admin", "admin", "local-admin", "staff"],
  },
  {
    route: {
      path: path.patientCreate.route,
      lazy: () =>
        import("@pages/panelPages/userManagement/patient/patientForm"),
      loader: ({ request }): IPatientLoaderData => {
        useUIStore.getState().setPage(
          request,
          "pageTitle.patientCreate",
          [
            {
              title: "pageTitle.patientManagement",
              url: path.patient.link(),
            },
          ],
          "create",
        );
        return { mode: "create" };
      },
    },
    permissions: ["super-admin", "admin", "local-admin"],
  },
  {
    route: {
      path: path.changePassword.route,
      lazy: () =>
        import("@pages/panelPages/changeUsersPassword/changeUsersPassword"),
      loader: ({ params, request }) => {
        useUIStore
          .getState()
          .setPage(request, "pageTitle.changePassword", [], "index");
        return { role: params.role, id: params.id };
      },
    },
    permissions: ["super-admin", "admin", "local-admin", "staff"],
  },
  {
    route: {
      path: path.roleManagement.route,
      lazy: () => import("@pages/panelPages/roleManagement/page"),
      loader: ({ request }) => {
        useUIStore
          .getState()
          .setPage(request, "pageTitle.roleManagement", [], "edit", "inner");
        return {};
      },
    },
    permissions: ["super-admin", "admin"],
  },
];
